/**
 * RF-gun keyboard behaviour for data entry panels (FH-235): Enter moves to the next field, and
 * Enter on the last field submits. A scanner sends Enter after every code, so a panel that needs a
 * mouse click to move on breaks scanning.
 *
 * Spread it on the panel's container:
 *   <Box {...enterAdvanceProps(submit)}>...fields...</Box>
 * Panels can nest (a create dialog opened from a form): only the nearest panel around the field acts.
 *
 * - An open dropdown/autocomplete list keeps Enter: it picks the highlighted option, nothing else.
 * - An autocomplete's own Enter (committing typed or scanned text) still runs; the move to the next
 *   field happens right after it.
 * - A field that takes several values (data-multi-value, e.g. MultiComboboxField): Enter with text
 *   typed only adds it, so the next value can be typed; Enter on the empty box moves on (FH-248).
 * - Text areas keep Enter for new lines; buttons keep Enter for clicking.
 * - Anything inside [data-enter-keep] keeps Enter for itself (the Smart Search box: Enter searches,
 *   also in a lookup opened from a panel, e.g. Inventory Add > Item).
 * - Enter on the last field with a required field still blank jumps to that field, and still calls
 *   submit so the panel names what's missing.
 * - { anyOne: true } is for lookups where any one field is enough (receipt # OR PO # OR dock door):
 *   Enter on a field with a value submits right away.
 */

const FIELD_SELECTOR = [
  'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="file"])',
  'select',
  'textarea',
  '[role="combobox"]:not(input)', // MUI Select's focusable element
].join(',');

const isUsable = (el) => !el.disabled
  && !el.readOnly
  && el.getAttribute('aria-disabled') !== 'true'
  && el.getAttribute('aria-readonly') !== 'true'
  && el.getAttribute('aria-hidden') !== 'true'
  && el.tabIndex >= 0
  && !el.closest('[aria-hidden="true"]')
  && (el.offsetParent !== null || el.getClientRects().length > 0);

/** The fields a user can type into, in screen order. */
export const entryFields = (container) => Array.from(container.querySelectorAll(FIELD_SELECTOR))
  .filter(isUsable)
  // radio groups and checkbox lists: one stop per group
  .filter((el, i, all) => !(el.type === 'radio' && el.name && all.findIndex((o) => o.type === 'radio' && o.name === el.name) !== i));

// MUI Select keeps its value (and its required flag) in a hidden native input next to the focusable div
const selectNative = (el) => (el.getAttribute('role') === 'combobox' && el.tagName !== 'INPUT' && el.parentElement
  ? el.parentElement.querySelector('input.MuiSelect-nativeInput') : null);

const isRequired = (el) => {
  const native = selectNative(el);
  return el.required || el.getAttribute('aria-required') === 'true' || Boolean(native && native.required);
};

const isEmpty = (el) => {
  if (el.type === 'checkbox' || el.type === 'radio') return false;
  if (el.getAttribute('role') === 'combobox' && el.tagName !== 'INPUT') {
    const native = selectNative(el);
    return native ? native.value === '' : false;
  }
  return el.value == null || String(el.value).trim() === '';
};

const focusField = (el) => {
  el.focus();
  if (typeof el.select === 'function' && el.tagName === 'INPUT' && el.type !== 'checkbox' && el.type !== 'radio') {
    try { el.select(); } catch (e) { /* not selectable (e.g. number in some browsers) */ }
  }
};

const advance = (container, current, submit, anyOne) => {
  const fields = entryFields(container);
  const i = fields.findIndex((f) => f === current || f.contains(current));
  if (i < 0) return;
  if (anyOne && !isEmpty(fields[i])) {
    if (submit) submit();
    return;
  }
  if (i < fields.length - 1) {
    focusField(fields[i + 1]);
    return;
  }
  const blank = fields.find((f) => isRequired(f) && isEmpty(f));
  if (blank && blank !== current) focusField(blank);
  if (submit) submit();
};

export const handleEnterAdvance = (e, submit, { anyOne = false } = {}) => {
  if (e.key !== 'Enter' || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
  if (e.nativeEvent && e.nativeEvent.isComposing) return;
  const t = e.target;
  if (!t || t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON' || t.tagName === 'A') return;
  if (t.closest && t.closest('[role="listbox"], [role="menu"], [role="option"]')) return;
  if (t.getAttribute('aria-expanded') === 'true') return; // open list: Enter picks the option
  // A box that uses Enter itself (the Smart Search box of a lookup opened from a panel: Enter searches)
  if (t.closest && t.closest('[data-enter-keep]')) return;
  const container = e.currentTarget;
  // Only the nearest panel acts: an outer panel sees a nested panel's keys first (capture phase, and
  // React passes portal events -- dialogs -- through their parent panels).
  const nearest = t.closest && t.closest('[data-enter-advance]');
  if (nearest && nearest !== container) return;
  if (!nearest && !container.contains(t)) return;

  // Never let the browser submit a surrounding <form> on its own; the last field decides.
  e.preventDefault();

  if (t.getAttribute('data-multi-value') === 'true' && String(t.value || '').trim() !== '') return;

  if (t.tagName === 'INPUT' && t.getAttribute('role') === 'combobox') {
    // Autocomplete: let it commit the typed/scanned text first, then move on.
    setTimeout(() => advance(container, t, submit, anyOne), 0);
    return;
  }
  e.stopPropagation(); // a closed MUI Select would otherwise open its list
  advance(container, t, submit, anyOne);
};

/** Props for a panel container: marks it and handles Enter (see top of file). */
export const enterAdvanceProps = (submit, opts) => ({
  'data-enter-advance': '',
  onKeyDownCapture: (e) => handleEnterAdvance(e, submit, opts),
});

export default handleEnterAdvance;
